import { Box, Stack, TableCell, Typography } from "@mui/material";

import { useTheme } from "@mui/material/styles";

import React from "react";
import { LoadBasketRead } from "src/store/api/generatedApi";
import { useCraneDriver } from "../../CraneDriverProvider";
import { BasketLayer, MaterialCompletionArgs } from "../../types";
import { createMaterialKey } from "../../utils";
import { CraneCellDecreaseButton } from "./CraneCellDecreaseButton";
import { CraneCellIncreaseButton } from "./CraneCellIncreaseButton";
import { CraneCellSubmitButton } from "./CraneCellSubmitButton";
import { CraneCellValue } from "./CraneCellValue";
import { CraneHoldToCompleteDialog } from "./CraneHoldToCompleteDialog";

interface CraneTableCellProps {
  basket: LoadBasketRead;
  layer: BasketLayer;
}

export const CraneTableCell = ({ basket, layer }: CraneTableCellProps) => {
  const { basketMaterials, submittingMaterialIds } = useCraneDriver();
  const theme = useTheme();

  const [showHoldDialog, setShowHoldDialog] = React.useState(false);

  const { materialName, layerNumber, materialId, targetMass, unit } = layer;

  const keyString = createMaterialKey(
    basket.load_basket_id,
    materialName,
    layerNumber
  );
  const savedAddition = basket.additions.find(
    (addition) =>
      addition.material_name === materialName &&
      addition.layer_number === layerNumber
  );
  const filledMass =
    savedAddition?.quantity.value ??
    basketMaterials[keyString]?.mass ??
    targetMass;
  const isCompleted = Boolean(savedAddition);
  const isSubmitting = submittingMaterialIds.has(keyString);

  const completeArgs: MaterialCompletionArgs = {
    basketId: basket.load_basket_id,
    materialId,
    layerNumber,
    currentMass: filledMass,
    materialName,
  };

  return (
    <>
      <TableCell
        className="material-cell"
        data-loadbasketid={basket.load_basket_id}
        data-materialname={materialName}
        data-layernumber={layerNumber}
        sx={{
          backgroundColor: isCompleted
            ? theme.palette.success.light
            : undefined,
          transition: "background-color 0.3s ease",
          p: { xs: 1, md: 1.5, lg: 2 },
          minWidth: { xs: 280, md: 350, lg: 420 },
          maxWidth: { lg: 500 },
          opacity: targetMass === 0 ? 0.5 : 1,
        }}
      >
        <Stack direction="column" spacing={{ xs: 0.5, md: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 32, lg: 42 },
              fontWeight: "bold",
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {materialName}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
            }}
          >
            <Box />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={{ xs: 1, md: 1.5, lg: 2 }}
            >
              <CraneCellDecreaseButton
                args={{
                  basketId: basket.load_basket_id,
                  materialName,
                  layerNumber,
                  targetMass,
                }}
                disabled={isCompleted || isSubmitting}
              />

              <CraneCellValue
                filledMass={filledMass}
                mass={targetMass}
                unit={unit}
              />

              <CraneCellIncreaseButton
                args={{
                  basketId: basket.load_basket_id,
                  materialName,
                  layerNumber,
                  targetMass,
                }}
                disabled={isCompleted || isSubmitting}
              />
            </Stack>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <CraneCellSubmitButton
                completeArgs={completeArgs}
                isCompleted={isCompleted}
                isSubmitting={isSubmitting}
                setShowHoldDialog={setShowHoldDialog}
              />
            </Box>
          </Box>
        </Stack>
      </TableCell>
      <CraneHoldToCompleteDialog
        open={showHoldDialog}
        onClose={() => {
          setShowHoldDialog(false);
        }}
      />
    </>
  );
};
